import apiClient from '@shared/api/client';
import { ENDPOINTS } from '@shared/api/endpoints';
import { mockBoardListResponse, mockTopicListByBoard, mockCommentListByTopic } from './mockLiveCommentData';

const USE_MOCK = false;

const delay = (ms = 400) => new Promise(resolve => setTimeout(resolve, ms));

// mock 狀態（in-memory，頁面重整會重置）
const _mockData = Object.fromEntries(
  Object.entries(mockCommentListByTopic).map(([k, v]) => [
    k,
    { ...v, items: v.items.map(c => ({ ...c })), top_items: v.top_items.map(c => ({ ...c })) },
  ])
);
let _nextId = 100;

/**
 * 取得 board 列表
 * POST /v1/interact/board/list
 * 回傳: { success, data: { items, total_items, current_page, total_pages, ... } }
 */
export const getBoardList = async (params = {}) => {
  if (USE_MOCK) {
    await delay(300);
    return { success: true, data: mockBoardListResponse };
  }
  try {
    const res = await apiClient.post(ENDPOINTS.LIVE_COMMENT.BOARD_LIST, {
      visible: true,
      per_page: 50,
      sort: 1,
      desc_sort: true,
      ...params,
    });
    return { success: true, data: res };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * 取得指定 board 的 topic 列表（含 board_details）
 * POST /v1/interact/board/topic/list
 * 回傳: { success, data: { board_details, board_id, items, total_items, ... } }
 */
export const getTopicList = async (boardId, params = {}) => {
  if (USE_MOCK) {
    await delay(300);
    const res = mockTopicListByBoard[Number(boardId)];
    return { success: !!res, data: res ?? null };
  }
  try {
    const res = await apiClient.post(ENDPOINTS.LIVE_COMMENT.TOPIC_LIST, {
      board_id: Number(boardId),
      visible: true,
      per_page: 100,
      desc_sort: true,
      ...params,
    });
    return { success: true, data: res };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * 取得指定 topic 的留言列表
 * POST /v1/comment/list
 * @param {number|string} topicId
 * @param {object} params - 額外參數（desc_sort, per_page, sort, page 等）
 * 回傳: { success, data: { items, top_items, topic_details, total_items, ... } }
 */
export const getCommentList = async (topicId, params = {}) => {
  if (USE_MOCK) {
    await delay(300);
    const res = _mockData[String(topicId)] ?? _mockData[Number(topicId)];
    if (!res) return { success: true, data: { items: [], top_items: [], topic_details: null, topic_id: Number(topicId), total: 0, total_items: 0, total_pages: 0, top_total: 0 } };
    return { success: true, data: { ...res } };
  }
  try {
    const res = await apiClient.post(ENDPOINTS.LIVE_COMMENT.COMMENT_LIST, {
      topic_id: Number(topicId),
      approved: true,
      sort: 1,
      per_page: 10000,
      page: 1,
      ...params,
    });
    return { success: true, data: res };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * 輪詢新留言
 * POST /v1/comment/polling/list
 */
export const pollCommentList = async (topicId, params = {}) => {
  if (USE_MOCK) {
    await delay(300);
    const res = _mockData[String(topicId)] ?? _mockData[Number(topicId)];
    if (!res) return { success: true, data: { items: [], top_items: [], total_items: 0 } };
    return { success: true, data: { ...res } };
  }
  try {
    const res = await apiClient.post(ENDPOINTS.LIVE_COMMENT.COMMENT_POLLING_LIST, {
      topic_id: Number(topicId),
      approved: true,
      sort: 1,
      per_page: 10000,
      page: 1,
      ...params,
    });
    return { success: true, data: res };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/** 送出留言（送出後進入 pending，需等審核） */
export const addComment = async (topicId, content, name) => {
  if (USE_MOCK) {
    await delay(500);
    const comment_id = _nextId++;
    setTimeout(() => {
      const key = String(topicId);
      if (!_mockData[key]) _mockData[key] = { items: [], top_items: [] };
      _mockData[key].items.push({ comment_id, content, commenter_name: name, created_at: new Date().toUTCString(), dharma_name: '無', like_count: 0, user_id: 0, user_name: 'You', approved: true, rank: null });
    }, 2000);
    return { success: true };
  }
  try {
    const res = await apiClient.post(ENDPOINTS.LIVE_COMMENT.COMMENT_ADD, { topic_id: Number(topicId), content, name });
    return { success: true, comment_id: res?.comment_id };
  } catch (e) {
    return { success: false, error: e.message, result_code: e.data?.result_code ?? e.status };
  }
};

/** 對留言按讚 */
export const likeComment = async (commentId) => {
  if (USE_MOCK) {
    await delay(200);
    for (const data of Object.values(_mockData)) {
      const c = [...data.items, ...data.top_items].find(x => x.comment_id === commentId);
      if (c) { c.like_count += 1; break; }
    }
    return { success: true };
  }
  try {
    const res = await apiClient.post(ENDPOINTS.LIVE_COMMENT.COMMENT_LIKE, { comment_id: commentId, like: true });
    return { success: true, like_count: res?.like_count };
  } catch (e) {
    return { success: false, error: e.message };
  }
};
